import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  SecurityScanOutlined,
  MailOutlined,
  MobileOutlined,
  QrcodeOutlined,
  WindowsOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import "./TwoFactorAuth.css";
import apiClient from "../services/apiClient";
import QRCode from "qrcode";
import { isAdmin } from "../utils/roleUtils";
import greenBg from "../assets/img/green_bg.jpg";
import siLogo from "../assets/img/si_logo.png";
import emailIcon from "../assets/img/2fa_email.png";
import phoneIcon from "../assets/img/2fa_phone.png";
import googleIcon from "../assets/img/2fa_google.png";
import microsoftIcon from "../assets/img/2fa_Microsoft.png";

const TwoFactorAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [methods, setMethods] = useState([]);
  const [userEmail, setUserEmail] = useState("");
  const [step, setStep] = useState("setup"); // setup, verify
  const [isLoading, setIsLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const baseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

  useEffect(() => {
    // Get selected method and email from navigation state
    if (location.state?.selectedMethod) {
      setSelectedMethod(location.state.selectedMethod);
      setUserEmail(location.state.email || "");
    } else {
      // If no method selected, we'll show methods selection
      setUserEmail(location.state?.email || "");
    }

    // Fetch 2FA methods
    fetchTwoFAMethods();
  }, [location.state, navigate]);

  const fetchTwoFAMethods = async () => {
    try {
      const token = localStorage.getItem("authToken");

      console.log(
        "TwoFactorAuth - Fetching 2FA methods with token:",
        token ? "Token exists" : "No token"
      );
      console.log("TwoFactorAuth - Base URL:", baseUrl);

      const response = await apiClient.get(`${baseUrl}/2fa/methods`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("TwoFactorAuth - 2FA Methods response:", response.data);

      // Handle new API response structure with nested data
      if (response.data.success && response.data.data) {
        setMethods(response.data.data);
      }
    } catch (error) {
      console.error("TwoFactorAuth - Error fetching 2FA methods:", error);
      console.error("TwoFactorAuth - Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleBackToMethods = () => {
    setSelectedMethod(null);
    setStep("setup");
    setVerificationCode("");
    setPhoneNumber("");
    setQrCodeDataUrl("");
  };

  const getMethodIcon = (code) => {
    switch (code) {
      case "email":
        return <img src={emailIcon} alt="Email OTP" className="method-icon-img" />;
      case "phone":
        return <img src={phoneIcon} alt="Phone OTP" className="method-icon-img" />;
      case "google":
        return <img src={googleIcon} alt="Google Authenticator" className="method-icon-img" />;
      case "microsoft":
        return <img src={microsoftIcon} alt="Microsoft Authenticator" className="method-icon-img" />;
      default:
        return <SecurityScanOutlined />;
    }
  };

  const getMethodSetupInstructions = (method) => {
    switch (method.code) {
      case "email":
        return {
          title: "Email OTP Setup",
          description: "We'll send a verification code to your email address.",
          steps: [
            "Click 'Setup 2FA' to receive a verification code",
            "Enter the 6-digit code from your email",
            "Click 'Verify & Enable 2FA' to complete setup",
          ],
        };
      case "phone":
        return {
          title: "Phone OTP Setup",
          description: "We'll send a verification code to your phone number.",
          steps: [
            "Enter your phone number below",
            "Click 'Setup 2FA' to receive a verification code",
            "Enter the 6-digit code from your SMS",
            "Click 'Verify & Enable 2FA' to complete setup",
          ],
        };
      case "google":
        return {
          title: "Google Authenticator Setup",
          description: "Scan the QR code with your Google Authenticator app.",
          steps: [
            "Click 'Setup 2FA' to proceed",
            "Download 'Google Authenticator App' if you don't have it",
            "Open the app and tap 'Add Account' or '+'",
            "Scan the QR code below",
            "Enter the 6-digit code from your app",
          ],
        };
      case "microsoft":
        return {
          title: "Microsoft Authenticator Setup",
          description:
            "Scan the QR code with your Microsoft Authenticator app.",
          steps: [
            "Click 'Setup 2FA' to proceed",
            "Download 'Microsoft Authenticator app' if you don't have it",
            "Open the app and tap 'Add Account' or '+'",
            "Scan the QR code below",
            "Enter the 6-digit code from your app",
          ],
        };
      default:
        return {
          title: "2FA Setup",
          description: "Set up two-factor authentication for your account.",
          steps: [],
        };
    }
  };

  const generateQRCode = async (qrCodeUrl) => {
    try {
      const dataUrl = await QRCode.toDataURL(qrCodeUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: isDark ? "#ffffff" : "#000000",
          light: isDark ? "#1f1f1f" : "#ffffff",
        },
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast.error("Failed to generate QR code", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    }
  };

  const handleSetup2FA = async () => {
    // Validate phone number for phone OTP
    if (selectedMethod.code === "phone" && !phoneNumber) {
      toast.error("Please enter your phone number", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = { method: selectedMethod.code };

      // Add phone number for phone OTP
      if (selectedMethod.code === "phone") {
        requestBody.phoneNumber = phoneNumber;
      }

      const response = await apiClient.post(`${baseUrl}/2fa/setup`, requestBody);

      console.log("2FA Setup API Response:", response.data);
      console.log("Response success:", response.data.success);
      console.log("Response error:", response.data.error);

      // Handle new API response structure with nested data
      const responseData = response.data.success && response.data.data ? response.data.data : response.data;

      if (response.data.success) {
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: isDark ? "dark" : "light",
        });
        if (
          selectedMethod.code === "google" ||
          selectedMethod.code === "microsoft"
        ) {
          setQrCode(responseData.qrCodeUrl);
          setSecretKey(responseData.secret);
          // Generate QR code from the URL
          await generateQRCode(responseData.qrCodeUrl);
        }
        setStep("verify");
      } else {
        // Handle API failure response
        const errorMessage = response.data.error || response.data.message || "Failed to setup 2FA";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: isDark ? "dark" : "light",
        });
      }


    } catch (error) {
      console.error("Error setting up 2FA:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);

      // Handle HTTP error responses (4xx, 5xx)
      let errorMessage = "Failed to setup 2FA";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit verification code", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post(
        `${baseUrl}/2fa/enable-2fa`,
        {
          method: selectedMethod.code,
          code: verificationCode,
        }
      );

      console.log("2FA Enable API Response:", response.data);
      console.log("Response success:", response.data.success);
      console.log("Response error:", response.data.error);

      if (response.data.success) {
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: isDark ? "dark" : "light",
        });

        // Navigate based on user role
        const isUserAdmin = isAdmin();
        console.log("TwoFactorAuth - User is admin:", isUserAdmin);

        if (isUserAdmin) {
          console.log("TwoFactorAuth - Navigating to admin dashboard");
          navigate("/admin");
        } else {
          console.log("TwoFactorAuth - Navigating to regular user dashboard");
          navigate("/d/dash");
        }
      } else {
        // Handle API failure response
        const errorMessage = response.data.error || response.data.message || "Invalid verification code. Please try again.";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: isDark ? "dark" : "light",
        });
      }
    } catch (error) {
      console.error("Error enabling 2FA:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);

      // Handle HTTP error responses (4xx, 5xx)
      let errorMessage = "Invalid verification code. Please try again.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If no method selected, show methods selection
  if (!selectedMethod) {
    return (
      <div className="twofa-container-centered">
        {/* Centered Content */}
        <div className="twofa-centered-content">
          <div className="centered-header">
            <h1 className="main-title">CHOOSE 2FA METHOD</h1>
            <p className="main-subtitle">
              Choose a method from below to continue with Two Factor Authentication
            </p>
          </div>

          <div className="methods-grid-centered">
            {methods.map((method) => (
              <div
                key={method.id}
                className={`method-card ${method.enabled ? "enabled" : "disabled"}`}
                onClick={() => method.enabled && handleMethodSelect(method)}
              >
                <div className="method-icon">
                  {getMethodIcon(method.code)}
                </div>
                <div className="method-content">
                  <h4 className="method-name">{method.name.toUpperCase()}</h4>
                  <p className="method-description">{method.description}</p>
                </div>
                {!method.enabled && (
                  <div className="method-disabled">
                    <span>Coming Soon</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const instructions = getMethodSetupInstructions(selectedMethod);

  return (
    <div className="twofa-container">
      {/* Left Side - Steps with Green Background */}
      <div className="twofa-left-section">
        <div className="left-background" style={{ backgroundImage: `url(${greenBg})` }}>
          {/* <div className="left-logo">
            <img src={siLogo} alt="SimplyBond" className="si-logo" />
          </div> */}
          <div className="twofa-steps-container">
            {instructions.steps.map((step, index) => (
              <div key={index} className="twofa-step-card">
                <div className="twofa-step-number">{index + 1}</div>
                <div className="step-text">
                  {step.split(/(\'[^']+\')/).map((part, partIndex) => {
                    if (part.startsWith("'") && part.endsWith("'")) {
                      return <span key={partIndex} className="quoted-text">{part}</span>;
                    }
                    return part;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Setup Content */}
      <div className="twofa-right-section">
        <div className="right-content">
          <h1 className="main-title">{instructions.title.toUpperCase()}</h1>
          <p className="main-subtitle">{instructions.description}</p>

          <div className="setup-card">
            <div className="setup-icon">
              {getMethodIcon(selectedMethod.code)}
            </div>
            <h2 className="setup-card-title">Two Factor Authentication</h2>
            <p className="setup-card-description">Configure your 2FA setup</p>

            {step === "setup" && (
              <div className="setup-form">
                {/* Phone Number Input for Phone OTP */}
                {selectedMethod.code === "phone" && (
                  <div className="form-group">
                    <label className="form-label">
                      <MobileOutlined />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="form-input"
                      placeholder="Enter your phone number"
                      value={phoneNumber}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/\D/g, '');
                        setPhoneNumber(value);
                      }}
                    />
                  </div>
                )}

                {/* Setup Button */}
                <button
                  className="setup-btn"
                  onClick={handleSetup2FA}
                  disabled={isLoading}
                >
                  {isLoading ? "Setting up..." : "Setup 2FA"}
                </button>
              </div>
            )}

            {step === "verify" && (
              <div className="verification-content">
                {/* QR Code Section for Google/Microsoft */}
                {(selectedMethod.code === "google" ||
                  selectedMethod.code === "microsoft") &&
                  qrCodeDataUrl && (
                    <div className="qr-code-section">
                      <h3 className="qr-title">Scan QR Code</h3>
                      <div className="qr-code-container">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR Code"
                          className="qr-code-image"
                        />
                      </div>
                    </div>
                  )}

                {/* Verification Code Input */}
                <div className="verification-section">
                  <h3 className="verification-title">Verification Code</h3>
                  <div className="verification-input-container">
                    <input
                      type="password"
                      className="verification-input-single"
                      placeholder="* * * * * *"
                      value={verificationCode}
                      onChange={(e) =>
                        setVerificationCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                  <button
                    className="verify-btn"
                    onClick={handleVerify2FA}
                    disabled={isLoading || verificationCode.length !== 6}
                  >
                    {isLoading ? "Verifying..." : "Verify & Enable 2FA"}
                  </button>
                </div>
              </div>
            )}

            <button
              className="back-link"
              onClick={handleBackToMethods}
              disabled={isLoading}
            >
              Back to methods
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
